import { Trans, useTranslation } from 'react-i18next';

import { BrandsList } from '~/components/BrandsList/BrandsList';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import { selectCurrentOrganizationId } from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import {
  BackdropOverrides,
  Container,
  PerkIncludedIcon,
  PlanPriceAmount,
  PlanPriceSufix,
} from '../TrialExpiredModal/TrialExpiredModal.styled';

import * as S from './UnlockFeaturesModal.styled';

export const UnlockFeaturesModal = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'unlockFeaturesModal' });
  const modal = useModal();
  const appRoute = useAppRoutes('organizations');
  const organizationId = useAppSelector(selectCurrentOrganizationId);

  const goToSubscription = () => {
    analytics.trackEvent('upgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.CONTEXT]: 'popup',
    });
    modal.hide();
    appRoute.push('subscription', { organizationId });
  };

  return (
    <Container>
      <BackdropOverrides />
      <S.TitleContainer>
        <S.BoltImage />
        <S.Title>{t('title', 'Unlock your full testing potential')}</S.Title>
        <S.Subtitle>
          {t(
            'subtitle',
            'Upgrade to access essential testing features and streamline your QA workflow.',
          )}
        </S.Subtitle>
      </S.TitleContainer>
      <S.PlansContainer>
        <S.Plan>
          <S.Ribbon>{t('plan.pro.badge', 'Popular')}</S.Ribbon>
          <S.PlanName>{t('plan.pro.name', 'PRO plan')}</S.PlanName>
          <S.PlanPrice>
            <PlanPriceAmount>
              $99 <PlanPriceSufix>/ {t('month', 'mo')}</PlanPriceSufix>
            </PlanPriceAmount>
          </S.PlanPrice>
          <S.PlanPerksContainer>
            <S.PlanPerk>
              <PerkIncludedIcon />
              <Trans i18nKey="plan.pro.perks.unlimitedCloudRuns">
                <b>Unlimited</b> cloud runs
              </Trans>
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.pro.perks.schedules', 'Schedules')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.pro.perks.cicd', 'CI/CD integrations')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.pro.perks.variablesAndProfiles', 'Variables & profiles')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.pro.perks.reporting', 'Reporting')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.pro.perks.inbox', 'Inbox for e-mail testing')}
            </S.PlanPerk>
          </S.PlanPerksContainer>
          <S.PlanButton variant="primary" onClick={goToSubscription} iconName="upgrade">
            {t('upgradeButton', 'Upgrade now')}
          </S.PlanButton>
        </S.Plan>
        <S.Plan>
          <S.PlanName>{t('plan.business.name', 'BUSINESS plan')}</S.PlanName>
          <S.PlanPrice>
            <PlanPriceAmount>
              $299 <PlanPriceSufix>/ {t('month', 'mo')}</PlanPriceSufix>
            </PlanPriceAmount>
          </S.PlanPrice>
          <S.PlanPerksContainer>
            <S.PlanPerk>
              <b>{t('plan.business.perks.everythingFromPro', 'Everything in PRO, and:')}</b>
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.business.perks.apiAccess', 'API access')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.business.perks.scheduleEveryMiunte', 'Schedule every minute')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.business.perks.longerCloudRunTime', 'Longer cloud run time')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.business.perks.testsHistory', '2 months of tests history')}
            </S.PlanPerk>
            <S.PlanPerk>
              <PerkIncludedIcon />
              {t('plan.business.perks.customerSupport', 'Priority customer support')}
            </S.PlanPerk>
          </S.PlanPerksContainer>
          <S.PlanButton variant="primary" onClick={goToSubscription} iconName="upgrade">
            {t('upgradeButton', 'Upgrade now')}
          </S.PlanButton>
        </S.Plan>
      </S.PlansContainer>
      <S.Footer>
        <p>{t('joinCompanies', 'Join 300+ companies that already upgraded')}</p>
        <BrandsList />
      </S.Footer>
    </Container>
  );
};

export default UnlockFeaturesModal;
